import { useQuery } from '@tanstack/react-query'
import { BookOpen, FlameIcon, TestTube2, Trophy } from 'lucide-react'
import { statisticsApi } from '@/api/statistics'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { LevelBreakdown } from '@/components/progress/LevelBreakdown'
import { ProgressChart } from '@/components/progress/ProgressChart'
import { StreakCalendar } from '@/components/progress/StreakCalendar'
import { AchievementsPanel } from '@/components/achievements/AchievementsPanel'
import { Card } from '@/components/ui/Card'

function formatStudyTime(minutes: number): string {
  if (minutes < 60) return `${minutes}m`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

interface StatCardProps {
  icon: React.ReactNode
  label: string
  value: string | number
  sub?: string
}

function StatCard({ icon, label, value, sub }: StatCardProps) {
  return (
    <Card className="flex items-center gap-3 p-3 sm:gap-4 sm:p-5">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary sm:h-10 sm:w-10">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-lg font-bold sm:text-2xl">{value}</p>
        <p className="text-xs text-muted-foreground sm:text-sm">{label}</p>
        {sub && <p className="text-[10px] text-muted-foreground sm:text-xs">{sub}</p>}
      </div>
    </Card>
  )
}

export function ProgressPage() {
  const overviewQuery = useQuery({
    queryKey: ['statistics', 'overview'],
    queryFn: () => statisticsApi.getOverview(),
  })

  const streakQuery = useQuery({
    queryKey: ['statistics', 'streak'],
    queryFn: () => statisticsApi.getStreakInfo(),
  })

  if (overviewQuery.isLoading || streakQuery.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (overviewQuery.isError || !overviewQuery.data) {
    return (
      <EmptyState
        title="Failed to load statistics"
        description="Something went wrong loading your progress data."
      />
    )
  }

  const overview = overviewQuery.data
  const streak = streakQuery.data

  return (
    <div className="space-y-8">
      {/* Top stats row */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
        <StatCard
          icon={<Trophy className="h-4 w-4 sm:h-5 sm:w-5" />}
          label="Words learned"
          value={overview.wordsLearned}
        />
        <StatCard
          icon={<BookOpen className="h-4 w-4 sm:h-5 sm:w-5" />}
          label="Articles read"
          value={overview.articlesRead}
        />
        <StatCard
          icon={<TestTube2 className="h-4 w-4 sm:h-5 sm:w-5" />}
          label="Tests completed"
          value={overview.testsCompleted}
        />
        <StatCard
          icon={<FlameIcon className="h-4 w-4 sm:h-5 sm:w-5" />}
          label="Current streak"
          value={`${overview.currentStreak} days`}
          sub={`Best: ${overview.longestStreak} days`}
        />
      </div>

      {/* Charts + Vocabulary breakdown — 2 columns on large screens */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Charts */}
        <Card className="p-6 lg:col-span-2">
          <h2 className="mb-4 text-base font-semibold">Activity charts</h2>
          <ProgressChart />
        </Card>

        {/* Vocabulary by CEFR level */}
        <Card className="p-6">
          <h2 className="mb-4 text-base font-semibold">Vocabulary by level</h2>
          <LevelBreakdown vocabularyByLevel={overview.vocabularyByLevel} />
          <p className="mt-4 text-xs text-muted-foreground">
            Total study time:{' '}
            <span className="font-medium text-foreground">
              {formatStudyTime(overview.totalStudyTimeMin)}
            </span>
          </p>
        </Card>
      </div>

      {/* Streak calendar */}
      {streak && (
        <Card className="p-6">
          <h2 className="mb-4 text-base font-semibold">Activity calendar</h2>
          <StreakCalendar
            history={streak.streakHistory}
            currentStreak={streak.currentStreak}
            longestStreak={streak.longestStreak}
          />
        </Card>
      )}

      <AchievementsPanel />
    </div>
  )
}
